import { Link } from "react-router-dom";
import "./landing.css";
import { ButtonLanding } from "../components/ui/ButtonLanding";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Newspaper, 
  CheckCircle, 
  Search, 
  Sparkles, 
  Shield, 
  Zap, 
  Globe, 
  ArrowRight,
  Play,
  Star,
  TrendingUp,
  Users,
  Clock,
  Target,
  Lightbulb,
  BookOpen,
  Award,
} from "lucide-react";

const Landing = () => {
  const features = [
    {
      icon: Sparkles,
      title: "AI Article Generation",
      description: "Create high-quality news articles instantly with advanced AI models trained on journalistic standards and real-world data.",
      gradient: "from-violet-500 to-purple-500",
    },
    {
      icon: CheckCircle,
      title: "Fact Verification",
      description: "Verify claims and analyze credibility with AI-powered fact-checking across multiple reliable sources in real-time.",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: Search,
      title: "Bias Detection",
      description: "Identify and eliminate bias patterns with advanced linguistic analysis and actionable improvement recommendations.",
      gradient: "from-pink-500 to-rose-500",
    },
    {
      icon: Shield,
      title: "Secure & Trusted",
      description: "Enterprise-grade security with encrypted data and full compliance with international journalism standards.",
      gradient: "from-emerald-500 to-teal-500",
    },
    {
      icon: Zap,
      title: "Real-Time Analysis",
      description: "Get instant feedback and intelligent suggestions as you write with our real-time AI-powered assistance.",
      gradient: "from-amber-500 to-orange-500",
    },
    {
      icon: Globe,
      title: "Multi-Language Support",
      description: "Create and verify content in 50+ languages with accurate translation and cultural localization.",
      gradient: "from-indigo-500 to-blue-500",
    },
  ];

  const stats = [
    { value: "10,000+", label: "Articles Generated", icon: BookOpen },
    { value: "98%", label: "Accuracy Rate", icon: Target },
    { value: "500+", label: "Active Journalists", icon: Users },
    { value: "24/7", label: "AI Availability", icon: Clock },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Senior Editor, Global News",
      content: "News Mind AI has transformed our newsroom. We're producing 3x more content without sacrificing quality.",
      rating: 5,
    },
    {
      name: "Michael Chen",
      role: "Investigative Journalist",
      content: "The fact-checking feature saved me countless hours. It's like having a research team at my fingertips.",
      rating: 5,
    },
    {
      name: "Emma Williams",
      role: "Chief Content Officer",
      content: "Our audience engagement increased by 45% since we started using News Mind AI. Simply incredible.",
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen overflow-hidden bg-background">
      {/* Animated Background */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        <div className="absolute left-1/4 top-0 h-96 w-96 animate-float rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-96 w-96 animate-float rounded-full bg-accent/10 blur-3xl" style={{ animationDelay: "1s" }} />
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="absolute inset-0 animate-glow rounded-xl bg-gradient-to-br from-primary to-accent opacity-75 blur" />
                <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent">
                  <Newspaper className="h-6 w-6 text-white" />
                </div>
              </div>
              <span className="font-display text-xl font-bold">News Mind AI</span>
            </div>
            
            <div className="hidden items-center gap-8 md:flex">
              <a href="#features" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
                Features
              </a>
              <a href="#how-it-works" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
                How It Works
              </a>
              <a href="#testimonials" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
                Testimonials
              </a>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/login">
                <ButtonLanding variant="ghost" className="font-medium">Sign In</ButtonLanding>
              </Link>
              <Link to="/register">
                <ButtonLanding className="group bg-gradient-to-r from-primary to-accent font-medium shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </ButtonLanding>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section (full-viewport, centered) */}
      <section className="landing-hero">
        <div className="w-full">
          <div className="mx-auto max-w-7xl px-6 py-24 md:py-32 hero-wrapper">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <Badge className="mb-4 border-primary/20 bg-primary/10 px-4 py-2 text-sm font-medium text-primary shadow-sm backdrop-blur-sm w-fit">
                  <Sparkles className="mr-2 h-4 w-4 inline" />
                  Trusted by 500+ News Organizations
                </Badge>

                <h1 className="font-display font-bold leading-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl hero-title">
                  Transform Journalism with <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">AI Intelligence</span>
                </h1>

                <p className="text-xl text-muted-foreground max-w-2xl leading-relaxed">
                  Generate, verify, and optimize news articles with cutting-edge AI. <span className="font-semibold text-foreground">Increase productivity by 300%</span> while ensuring accuracy and eliminating bias.
                </p>

                <div className="flex flex-wrap items-center gap-4 mt-8">
                  <Link to="/register">
                    <ButtonLanding size="lg" className="h-16 bg-gradient-to-r from-primary to-accent px-8 text-lg font-semibold shadow-xl hover:scale-105 transition-all duration-300 shadow-primary/25">
                      Start Free Trial
                      <ArrowRight className="ml-2 h-6 w-6" />
                    </ButtonLanding>
                  </Link>

                  <ButtonLanding size="lg" variant="outline" className="h-16 border-2 px-8 text-lg font-semibold hover:bg-primary/5 transition-all duration-300">
                    <Play className="mr-2 h-6 w-6" /> Watch Demo
                  </ButtonLanding>
                </div>

                <div className="mt-8 flex flex-wrap gap-8 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" /> No credit card required
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" /> 14-day free trial
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" /> Cancel anytime
                  </div>
                </div>
              </div>

              {/* Hero Visual/Illustration */}
              <div className="relative lg:block hidden">
                <div className="relative z-10">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl blur-3xl transform rotate-6 scale-110"></div>
                  <div className="relative bg-gradient-to-br from-background to-muted/50 border border-primary/10 rounded-3xl p-8 shadow-2xl backdrop-blur-sm">
                    <div className="space-y-6">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
                          <Newspaper className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">AI Article Generation</h3>
                          <p className="text-sm text-muted-foreground">Creating high-quality content</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full w-3/4 bg-gradient-to-r from-primary to-accent rounded-full animate-pulse"></div>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Processing article...</span>
                          <span>75%</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-primary/5 rounded-lg p-3">
                          <CheckCircle className="h-4 w-4 text-emerald-500 mb-1" />
                          <p className="text-xs font-medium">Fact-checked</p>
                        </div>
                        <div className="bg-accent/5 rounded-lg p-3">
                          <Shield className="h-4 w-4 text-blue-500 mb-1" />
                          <p className="text-xs font-medium">Bias-free</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Stats Section */}
      <section className="border-y bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50 py-20 backdrop-blur-sm">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold mb-4">Trusted by Industry Leaders</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Join thousands of journalists who have transformed their workflow with AI-powered tools</p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="group text-center transition-all duration-300 hover:scale-105 hover:-translate-y-2"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-accent text-white shadow-xl shadow-primary/25">
                    <Icon className="h-8 w-8" />
                  </div>
                  <div className="mb-3 font-display text-5xl font-bold text-foreground md:text-6xl bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">{stat.value}</div>
                  <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50"></div>
        <div className="container mx-auto px-4 relative">
          <div className="space-y-20">
            <div className="text-center">
              <Badge className="mb-6 border-primary/20 bg-primary/10 text-primary px-4 py-2">
                <Lightbulb className="mr-2 h-4 w-4" />
                Powerful Features
              </Badge>
              <h2 className="mb-6 font-display text-5xl font-bold sm:text-6xl">Everything You Need</h2>
              <p className="mx-auto max-w-3xl text-xl text-muted-foreground leading-relaxed">
                Comprehensive AI tools designed specifically for modern journalism workflows, delivering unparalleled efficiency and accuracy
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={index}
                    className="group relative overflow-hidden border-2 border-primary/10 bg-background/50 backdrop-blur-sm transition-all duration-500 hover:scale-105 hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/10"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br opacity-0 blur-3xl transition-all duration-500 group-hover:opacity-30"
                          style={{ backgroundImage: `linear-gradient(to bottom right, var(--tw-gradient-stops))` }} />
                    <CardContent className="relative p-8">
                      <div className={`mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${feature.gradient} shadow-xl shadow-primary/25`}>
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                      <h3 className="mb-4 font-display text-2xl font-bold">{feature.title}</h3>
                      <p className="text-muted-foreground leading-relaxed text-lg">{feature.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50"></div>
        <div className="container mx-auto px-4 relative">
          <div className="space-y-20">
            <div className="text-center">
              <Badge className="mb-6 border-primary/20 bg-primary/10 text-primary px-4 py-2">
                <Target className="mr-2 h-4 w-4" />
                Simple Process
              </Badge>
              <h2 className="mb-6 font-display text-5xl font-bold sm:text-6xl">How It Works</h2>
              <p className="mx-auto max-w-3xl text-xl text-muted-foreground leading-relaxed">
                Get started in three simple steps and transform your newsroom with AI-powered journalism tools
              </p>
            </div>

            <div className="relative">
              <div className="hidden lg:block absolute top-32 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

              <div className="grid gap-16 md:grid-cols-3 relative">
                {[
                  {
                    number: "01",
                    title: "Create Content",
                    description: "Enter your topic or paste existing content to generate AI-powered articles with perfect journalistic structure and compelling narratives",
                    icon: Sparkles,
                  },
                  {
                    number: "02",
                    title: "Verify & Analyze",
                    description: "Fact-check claims and detect bias with our advanced AI tools powered by multiple trusted sources and real-time verification",
                    icon: Shield,
                  },
                  {
                    number: "03",
                    title: "Publish with Confidence",
                    description: "Review, edit, and publish high-quality, verified content that engages your audience and builds trust",
                    icon: CheckCircle,
                  },
                ].map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <div key={index} className="relative text-center group">
                      <div className="relative z-10 mb-8 inline-flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent shadow-2xl shadow-primary/25 transition-transform group-hover:scale-110">
                        <Icon className="h-12 w-12 text-white" />
                      </div>
                      <div className="mb-4 font-display text-lg font-bold text-primary bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mx-auto">{step.number}</div>
                      <h3 className="mb-4 font-display text-3xl font-bold">{step.title}</h3>
                      <p className="text-muted-foreground text-lg leading-relaxed max-w-sm mx-auto">{step.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50"></div>
        <div className="container mx-auto px-4 relative">
          <div className="space-y-20">
            <div className="text-center">
              <Badge className="mb-6 border-primary/20 bg-primary/10 text-primary px-4 py-2">
                <Award className="mr-2 h-4 w-4" />
                Testimonials
              </Badge>
              <h2 className="mb-6 font-display text-5xl font-bold sm:text-6xl">Loved by Journalists</h2>
              <p className="mx-auto max-w-3xl text-xl text-muted-foreground leading-relaxed">
                See what industry professionals are saying about News Mind AI and how it's transformed their workflow
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              {testimonials.map((testimonial, index) => (
                <Card key={index} className="group border-2 border-primary/10 bg-background/50 backdrop-blur-sm transition-all duration-500 hover:scale-105 hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/10">
                  <CardContent className="p-8">
                    <div className="mb-6 flex gap-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-6 w-6 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="mb-8 text-muted-foreground text-lg leading-relaxed italic">"{testimonial.content}"</p>
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-bold text-lg">{testimonial.name}</div>
                        <div className="text-sm text-muted-foreground font-medium">{testimonial.role}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-50 via-pink-50 to-red-50"></div>
        <div className="container mx-auto px-4 relative">
          <Card className="relative overflow-hidden border-2 border-primary/20 bg-gradient-to-br from-background/80 via-primary/5 to-accent/5 backdrop-blur-sm shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5" />
            <CardContent className="relative p-16 text-center">
              <div className="mb-8 inline-flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent shadow-xl">
                <TrendingUp className="h-10 w-10 text-white" />
              </div>
              <h2 className="mb-6 font-display text-5xl font-bold sm:text-6xl">
                Ready to Transform Your Newsroom?
              </h2>
              <p className="mx-auto mb-10 max-w-3xl text-xl text-muted-foreground leading-relaxed">
                Join hundreds of journalists using AI to create better content faster.
                Start your free trial today and experience the future of journalism.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                <Link to="/register">
                  <ButtonLanding size="lg" className="group h-16 bg-gradient-to-r from-primary to-accent px-12 text-lg font-semibold shadow-2xl shadow-primary/25 transition-all hover:scale-105 hover:shadow-primary/40">
                    Start Your Free Trial
                    <ArrowRight className="ml-2 h-6 w-6 transition-transform group-hover:translate-x-1" />
                  </ButtonLanding>
                </Link>
                <ButtonLanding size="lg" variant="outline" className="h-16 border-2 px-12 text-lg font-semibold hover:bg-primary/5 transition-all duration-300">
                  Schedule Demo
                </ButtonLanding>
              </div>
              <p className="text-base text-muted-foreground font-medium">
                No credit card required · 14-day free trial · Cancel anytime
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-gradient-to-br from-muted/30 to-muted/10 py-20">
        <div className="container mx-auto px-4">
          <div className="grid gap-12 md:grid-cols-4">
            <div className="md:col-span-2">
              <div className="mb-6 flex items-center gap-3">
                <div className="relative">
                  <div className="absolute inset-0 animate-glow rounded-xl bg-gradient-to-br from-primary to-accent opacity-75 blur" />
                  <div className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent">
                    <Newspaper className="h-7 w-7 text-white" />
                  </div>
                </div>
                <span className="font-display text-2xl font-bold">News Mind AI</span>
              </div>
              <p className="text-base text-muted-foreground mb-6 leading-relaxed max-w-md">
                AI-powered journalism platform for the modern newsroom. Transform the way you create news with cutting-edge technology and unparalleled accuracy.
              </p>
              <div className="flex gap-4">
                <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center hover:bg-primary/20 transition-colors cursor-pointer">
                  <span className="text-primary font-bold text-sm">f</span>
                </div>
                <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center hover:bg-primary/20 transition-colors cursor-pointer">
                  <span className="text-primary font-bold text-sm">t</span>
                </div>
                <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center hover:bg-primary/20 transition-colors cursor-pointer">
                  <span className="text-primary font-bold text-sm">in</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="mb-6 font-bold text-lg">Product</h4>
              <ul className="space-y-4 text-base">
                <li><a href="#features" className="text-muted-foreground transition-colors hover:text-foreground">Features</a></li>
                <li><a href="#how-it-works" className="text-muted-foreground transition-colors hover:text-foreground">How It Works</a></li>
                <li><a href="#" className="text-muted-foreground transition-colors hover:text-foreground">Pricing</a></li>
                <li><a href="#" className="text-muted-foreground transition-colors hover:text-foreground">Documentation</a></li>
              </ul>
            </div>

            <div>
              <h4 className="mb-6 font-bold text-lg">Company</h4>
              <ul className="space-y-4 text-base">
                <li><a href="#" className="text-muted-foreground transition-colors hover:text-foreground">About Us</a></li>
                <li><a href="#" className="text-muted-foreground transition-colors hover:text-foreground">Blog</a></li>
                <li><a href="#" className="text-muted-foreground transition-colors hover:text-foreground">Careers</a></li>
                <li><a href="#" className="text-muted-foreground transition-colors hover:text-foreground">Contact</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-16 border-t pt-8 text-center">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-base text-muted-foreground">
                © 2025 News Mind AI. All rights reserved. Built with ❤️ for journalists worldwide.
              </p>
              <div className="flex gap-6 text-sm text-muted-foreground">
                <a href="#" className="hover:text-foreground transition-colors">Privacy Policy</a>
                <a href="#" className="hover:text-foreground transition-colors">Terms of Service</a>
                <a href="#" className="hover:text-foreground transition-colors">Cookie Policy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
